import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
  ShieldAlert,
  Check,
} from "lucide-react";
import { billingRulesApi } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";
import ConfirmDialog from "../ui/ConfirmDialog";
import InlineNotification, { NotificationState } from "../ui/InlineNotification";

// ─── Types ──────────────────────────────────────────────────────────────────────

interface BillingRule {
  _id: string;
  name: string;
  ruleType: string;
  config: Record<string, any>;
  severity: "warn" | "block";
  active: boolean;
  clientId?: any;
  createdAt?: string;
}

interface Props {
  onDataChange?: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const RULE_TYPE_LABELS: Record<string, string> = {
  block_billing_prohibited: "Block Billing Prohibited",
  min_narrative_length: "Minimum Narrative Length",
  max_hourly_rate: "Maximum Hourly Rate",
  max_daily_hours: "Maximum Daily Hours",
  prohibited_terms: "Prohibited Terms",
  required_task_codes: "Required Task Codes",
  min_increment: "Minimum Time Increment",
  max_entry_hours: "Maximum Entry Hours",
};

const RULE_TYPE_OPTIONS = Object.entries(RULE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const SEVERITY_COLORS: Record<string, string> = {
  warn: "bg-amber-100 text-amber-700",
  block: "bg-red-100 text-red-600",
};

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

const EMPTY_FORM = {
  name: "",
  ruleType: "block_billing_prohibited",
  severity: "warn" as "warn" | "block",
  config: {} as Record<string, any>,
};

// ─── Component ──────────────────────────────────────────────────────────────────

const BillingRulesTab: React.FC<Props> = ({ onDataChange }) => {
  const [rules, setRules] = useState<BillingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRule, setEditingRule] = useState<BillingRule | null>(null);
  const [saving, setSaving] = useState(false);

  // Notification & confirmation state
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [confirmState, setConfirmState] = useState<{ open: boolean; ruleId: string }>({ open: false, ruleId: "" });

  // Form state
  const [form, setForm] = useState({ ...EMPTY_FORM });

  // Dynamic config helpers
  const [configTerms, setConfigTerms] = useState("");
  const [configCodes, setConfigCodes] = useState("");

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingRulesApi.list();
      setRules(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch billing rules:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // ─── Form Helpers ───────────────────────────────────────────────────────────

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setConfigTerms("");
    setConfigCodes("");
    setShowCreateForm(false);
    setEditingRule(null);
  };

  const openCreate = () => {
    resetForm();
    setShowCreateForm(true);
  };

  const openEdit = (rule: BillingRule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      ruleType: rule.ruleType,
      severity: rule.severity,
      config: { ...rule.config },
    });
    // Populate text fields for array-based configs
    if (rule.ruleType === "prohibited_terms" && Array.isArray(rule.config?.terms)) {
      setConfigTerms(rule.config.terms.join(", "));
    } else {
      setConfigTerms("");
    }
    if (rule.ruleType === "required_task_codes" && Array.isArray(rule.config?.codes)) {
      setConfigCodes(rule.config.codes.join(", "));
    } else {
      setConfigCodes("");
    }
    setShowCreateForm(true);
  };

  const buildConfig = (): Record<string, any> => {
    const cfg: Record<string, any> = {};
    switch (form.ruleType) {
      case "min_narrative_length":
        cfg.minLength = Number(form.config.minLength) || 0;
        break;
      case "max_hourly_rate":
        cfg.maxRateCents = Math.round((Number(form.config.maxRateCents) || 0) * 100);
        break;
      case "max_daily_hours":
        cfg.maxHours = Number(form.config.maxHours) || 0;
        break;
      case "prohibited_terms":
        cfg.terms = configTerms
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        break;
      case "required_task_codes":
        cfg.codes = configCodes
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean);
        break;
      case "min_increment":
        cfg.incrementMinutes = Number(form.config.incrementMinutes) || 0;
        break;
      case "max_entry_hours":
        cfg.maxHours = Number(form.config.maxHours) || 0;
        break;
      // block_billing_prohibited has no config
    }
    return cfg;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        ruleType: form.ruleType,
        severity: form.severity,
        config: buildConfig(),
      };

      if (editingRule) {
        await billingRulesApi.update(editingRule._id, payload);
      } else {
        await billingRulesApi.create(payload);
      }
      resetForm();
      fetchRules();
      onDataChange?.();
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to save rule" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    const id = confirmState.ruleId;
    setConfirmState({ open: false, ruleId: "" });
    try {
      await billingRulesApi.remove(id);
      fetchRules();
      onDataChange?.();
      setNotification({ type: "success", message: "Billing rule deleted" });
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to delete rule" });
    }
  };

  const handleDelete = (id: string) => {
    setConfirmState({ open: true, ruleId: id });
  };

  const handleToggleActive = async (rule: BillingRule) => {
    try {
      await billingRulesApi.update(rule._id, { active: !rule.active });
      setRules((prev) =>
        prev.map((r) => (r._id === rule._id ? { ...r, active: !r.active } : r))
      );
      onDataChange?.();
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to toggle rule" });
    }
  };

  const setConfigField = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, config: { ...prev.config, [field]: value } }));
  };

  // ─── Config Fields Renderer ─────────────────────────────────────────────────

  const renderConfigFields = () => {
    switch (form.ruleType) {
      case "min_narrative_length":
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Minimum Characters
            </label>
            <input
              type="number"
              min={0}
              value={form.config.minLength || ""}
              onChange={(e) => setConfigField("minLength", e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="e.g. 50"
            />
          </div>
        );

      case "max_hourly_rate":
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Maximum Rate ($)
            </label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={form.config.maxRateCents || ""}
              onChange={(e) => setConfigField("maxRateCents", e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="e.g. 350.00"
            />
            <p className="text-xs text-slate-400 mt-1">Enter amount in dollars</p>
          </div>
        );

      case "max_daily_hours":
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Maximum Daily Hours
            </label>
            <input
              type="number"
              min={0}
              step={0.5}
              value={form.config.maxHours || ""}
              onChange={(e) => setConfigField("maxHours", e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="e.g. 10"
            />
          </div>
        );

      case "prohibited_terms":
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Prohibited Terms
            </label>
            <input
              type="text"
              value={configTerms}
              onChange={(e) => setConfigTerms(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="e.g. research, review, attention to"
            />
            <p className="text-xs text-slate-400 mt-1">Separate terms with commas</p>
          </div>
        );

      case "required_task_codes":
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Required Task Codes
            </label>
            <input
              type="text"
              value={configCodes}
              onChange={(e) => setConfigCodes(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="e.g. L110, L120, L130"
            />
            <p className="text-xs text-slate-400 mt-1">Separate codes with commas</p>
          </div>
        );

      case "min_increment":
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Minimum Increment (minutes)
            </label>
            <input
              type="number"
              min={0}
              step={1}
              value={form.config.incrementMinutes || ""}
              onChange={(e) => setConfigField("incrementMinutes", e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="e.g. 6"
            />
          </div>
        );

      case "max_entry_hours":
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Maximum Entry Hours
            </label>
            <input
              type="number"
              min={0}
              step={0.5}
              value={form.config.maxHours || ""}
              onChange={(e) => setConfigField("maxHours", e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="e.g. 8"
            />
          </div>
        );

      case "block_billing_prohibited":
      default:
        return null;
    }
  };

  // ─── Format config for display ──────────────────────────────────────────────

  const formatConfig = (rule: BillingRule): string => {
    const c = rule.config || {};
    switch (rule.ruleType) {
      case "min_narrative_length":
        return `Min ${c.minLength || 0} characters`;
      case "max_hourly_rate":
        return `Max $${((c.maxRateCents || 0) / 100).toFixed(2)}/hr`;
      case "max_daily_hours":
        return `Max ${c.maxHours || 0} hrs/day`;
      case "prohibited_terms":
        return Array.isArray(c.terms) ? c.terms.join(", ") : "";
      case "required_task_codes":
        return Array.isArray(c.codes) ? c.codes.join(", ") : "";
      case "min_increment":
        return `${c.incrementMinutes || 0} min increments`;
      case "max_entry_hours":
        return `Max ${c.maxHours || 0} hrs/entry`;
      default:
        return "";
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500 text-sm">Loading billing rules...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      <InlineNotification notification={notification} onDismiss={() => setNotification(null)} />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title="Delete Billing Rule"
        message="Are you sure you want to delete this billing rule? This action cannot be undone."
        confirmLabel="Delete Rule"
        variant="danger"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmState({ open: false, ruleId: "" })}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Billing Rules</h2>
          <p className="text-sm text-slate-500">
            Enforce billing compliance with outside counsel guidelines
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-colors"
          style={{ backgroundColor: PRIMARY }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = HOVER as string)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = PRIMARY as string)}
        >
          <Plus className="h-4 w-4" />
          Add Rule
        </button>
      </div>

      {/* Create / Edit Form */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-800">
                  {editingRule ? "Edit Rule" : "Create Rule"}
                </h3>
                <button
                  type="button"
                  onClick={resetForm}
                  className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Rule Name
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                    placeholder="e.g. No Block Billing"
                  />
                </div>

                {/* Rule Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Rule Type
                  </label>
                  <SearchableSelect
                    options={RULE_TYPE_OPTIONS}
                    value={form.ruleType}
                    onChange={(val) => {
                      setForm((p) => ({ ...p, ruleType: val || "block_billing_prohibited", config: {} }));
                      setConfigTerms("");
                      setConfigCodes("");
                    }}
                    placeholder="Select rule type"
                  />
                </div>

                {/* Severity */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Severity
                  </label>
                  <SearchableSelect
                    options={[
                      { value: "warn", label: "Warning" },
                      { value: "block", label: "Block" },
                    ]}
                    value={form.severity}
                    onChange={(val) =>
                      setForm((p) => ({ ...p, severity: (val || "warn") as "warn" | "block" }))
                    }
                    placeholder="Select severity"
                  />
                </div>

                {/* Dynamic config */}
                {renderConfigFields()}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-colors disabled:opacity-50"
                  style={{ backgroundColor: PRIMARY }}
                  onMouseEnter={(e) =>
                    !saving && (e.currentTarget.style.backgroundColor = HOVER as string)
                  }
                  onMouseLeave={(e) =>
                    !saving && (e.currentTarget.style.backgroundColor = PRIMARY as string)
                  }
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  {editingRule ? "Update Rule" : "Create Rule"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rules List */}
      {rules.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="flex flex-col items-center justify-center py-20"
        >
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
            <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
              <Shield className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No billing rules configured</h3>
          <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
            Enforce billing compliance with outside counsel guidelines. Set rules for narrative length, hourly rate caps, prohibited terms, and more.
          </p>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: PRIMARY }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = HOVER as string)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = PRIMARY as string)}
          >
            <Plus className="h-4 w-4" />
            Add Rule
          </button>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="text-left px-5 py-3 font-medium text-slate-500">Rule Name</th>
                  <th className="text-left px-5 py-3 font-medium text-slate-500">Type</th>
                  <th className="text-left px-5 py-3 font-medium text-slate-500">Config</th>
                  <th className="text-left px-5 py-3 font-medium text-slate-500">Severity</th>
                  <th className="text-center px-5 py-3 font-medium text-slate-500">Active</th>
                  <th className="text-right px-5 py-3 font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule, idx) => (
                  <motion.tr
                    key={rule._id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                  >
                    {/* Name */}
                    <td className="px-5 py-3.5 font-medium text-slate-800">{rule.name}</td>

                    {/* Type */}
                    <td className="px-5 py-3.5 text-slate-600">
                      {RULE_TYPE_LABELS[rule.ruleType] || rule.ruleType}
                    </td>

                    {/* Config summary */}
                    <td className="px-5 py-3.5 text-slate-500 text-xs max-w-[200px] truncate">
                      {formatConfig(rule) || <span className="text-slate-300">--</span>}
                    </td>

                    {/* Severity */}
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                          SEVERITY_COLORS[rule.severity] || "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {rule.severity === "block" ? (
                          <ShieldAlert className="h-3 w-3" />
                        ) : (
                          <AlertTriangle className="h-3 w-3" />
                        )}
                        {rule.severity === "block" ? "Block" : "Warn"}
                      </span>
                    </td>

                    {/* Active toggle */}
                    <td className="px-5 py-3.5 text-center">
                      <button
                        onClick={() => handleToggleActive(rule)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          rule.active ? "bg-emerald-500" : "bg-slate-300"
                        }`}
                        aria-label={rule.active ? "Deactivate rule" : "Activate rule"}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            rule.active ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-3.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEdit(rule)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Edit rule"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(rule._id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                          title="Delete rule"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BillingRulesTab;
